import { action, computed, makeObservable, observable } from 'mobx'
import { CallCenterJobSummary } from 'call-center/models/CallCenterJobSummary'
import JobSearchStore from 'jobs/views/JobManagement/JobSearchStore'
import JobSearchQueryStringBuilder from 'jobs/infra/JobSearchQueryStringBuilder'
import { JobScopeOption } from 'jobs/dtos/job'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { CursorPaged, CursorPageQuery } from 'types/definitions'
import { CancellationToken } from 'infra/cancellation'

const SEARCH_LIMIT = 25

export class CallCenterJobHistoryViewModel {
  @observable.ref
  clientUserPublicIds: string[] = []

  constructor(private jobSearchStore: JobSearchStore) {
    makeObservable(this)
  }

  @computed
  get jobs() {
    return this.jobsScroller.items
  }

  @action.bound
  setUsersAndLoadJobs(userPublicIds: string[]): void {
    this.jobsScroller.reset(false)
    this.clientUserPublicIds = userPublicIds

    void this.jobsScroller.fetch()
  }

  @action.bound
  clear() {
    this.jobsScroller.reset(false)
    this.clientUserPublicIds = []
  }

  jobsScroller = new InfiniteScrollProvider<CallCenterJobSummary>({
    limit: SEARCH_LIMIT,
    fetchItems: (paging, cancellationToken) =>
      this.fetchJobSummaries(
        paging,
        cancellationToken,
        this.clientUserPublicIds,
      ),
  })

  private async fetchJobSummaries(
    paging: CursorPageQuery,
    cancellationToken: CancellationToken,
    clientPublicIds: string[],
  ): Promise<CursorPaged<CallCenterJobSummary>> {
    if (clientPublicIds.length === 0) {
      return CursorPaged.empty
    }

    try {
      const response = await this.jobSearchStore.searchJobs({
        filter: new JobSearchQueryStringBuilder()
          .withMemberIds(clientPublicIds)
          .build(),
        $limit: paging.limit ?? SEARCH_LIMIT,
        scope: JobScopeOption.All,
        orderBy: 'date_created desc',
        after: paging.after ?? undefined,
      })

      cancellationToken.throwIfCancelled()

      const clientIdSet = new Set(clientPublicIds)
      const jobs = response.data
        .filter((job) => {
          const championClient = job.championClient
          if (championClient) {
            return clientIdSet.has(championClient.id)
          }

          return job.members.some(
            (member) => clientIdSet.has(member.id) && member.type === 'CLIENT',
          )
        })
        .map((job) => ({
          id: job.id,
          name: job.displayName,
          total: job.total,
          workspace: job.workspace,
          status: job.status,
          jobScopeItems: job.jobScopeItems || [],
          dateCreated: job.dateCreated,
          legendId: job.legendId,
          legendVersion: job.legendVersion,
        }))

      return {
        data: jobs,
        cursor: response.cursor || null,
        total: response.total,
      }
    } catch (error) {
      if (cancellationToken.isCancelled) {
        return CursorPaged.empty
      }
      throw error
    }
  }
}
